import React, { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Card,
  Grid,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Snackbar,
  FormHelperText,
  Alert,
  IconButton,
  Tooltip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";

import MDBox from "shared/components/MDBox";
import MDTypography from "shared/components/MDTypography";
import MDButton from "shared/components/MDButton";
import DataTable from "shared/components/Tables/DataTable";

import { useFormSchedule } from "../../hooks/useFormSchedule";

// Services
import { getAllCommerce } from "modules/bitacora/services/Configuracion/Comercio/comercioService";
import { getAllZone } from "modules/bitacora/services/Configuracion/Zona/zonaService";
import { getAllActivityType } from "modules/bitacora/services/Configuracion/TipoActividad/tipoActividadService";
import { getAllDevice } from "modules/bitacora/services/Configuracion/Equipo/equipoService";
import { getAllUsersService } from "modules/usuario/services/userService";

const FormSchedule = ({
  onSubmit,
  afterSubmit = () => {},
  initialData = {},
  isEditMode = false,
  loading: externalLoading = false,
}) => {
  // Hook personalizado
  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    isSubmitting,
    actividades,
    agregarActividad,
    eliminarActividad,
    submitError,
    submitSuccess,
    setSubmitError,
    setSubmitSuccess,
    setFieldValue, // Añadido para limpiar valores
  } = useFormSchedule(onSubmit, afterSubmit, initialData, isEditMode);

  const [commerces, setCommerces] = useState([]);
  const [zones, setZones] = useState([]);
  const [devices, setDevices] = useState([]);
  const [activityTypes, setActivityTypes] = useState([]);
  const [tecnicos, setTecnicos] = useState([]);
  const [filteredZones, setFilteredZones] = useState([]);
  const [filteredDevices, setFilteredDevices] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [errorData, setErrorData] = useState(null);

  const handleCloseSnackbar = () => {
    setSubmitError(null);
    setSubmitSuccess(false);
  };

  // Memoizar la función de carga de datos
  const loadInitialData = useCallback(async () => {
    try {
      const [commercesData, zonesData, devicesData, typeActivityData, technicalData] =
        await Promise.all([
          getAllCommerce(),
          getAllZone(),
          getAllDevice(),
          getAllActivityType(),
          getAllUsersService(),
        ]);

      setCommerces(Array.isArray(commercesData) ? commercesData : []);
      setZones(Array.isArray(zonesData) ? zonesData : []);
      setDevices(Array.isArray(devicesData) ? devicesData : []);
      setActivityTypes(Array.isArray(typeActivityData) ? typeActivityData : []);
      setTecnicos(
        Array.isArray(technicalData) ? technicalData.filter((t) => Number(t.roleId) === 2) : []
      );
    } catch (error) {
      setErrorData("Error al cargar datos");
      console.error("Error loading data:", error);
    } finally {
      setLoadingData(false);
    }
  }, []);

  // 🚀 Cargar datos iniciales
  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      await loadInitialData();
    };

    if (isMounted) {
      loadData();
    }

    return () => {
      isMounted = false;
    };
  }, [loadInitialData]);

  // Filtrar zonas basadas en el comercio seleccionado
  useEffect(() => {
    if (values.commerceId) {
      const filtered = zones.filter((z) => Number(z.commerceId) === Number(values.commerceId));
      setFilteredZones(filtered);
    } else {
      setFilteredZones([]);
    }
  }, [values.commerceId, zones]);

  // Filtrar dispositivos basados en la zona seleccionada
  useEffect(() => {
    if (values.zoneId) {
      const filtered = devices.filter((d) => Number(d.zoneId) === Number(values.zoneId));
      setFilteredDevices(filtered);
    } else {
      setFilteredDevices([]);
    }
  }, [values.zoneId, devices]);

  // Limpiar zona cuando cambia el comercio
  useEffect(() => {
    if (!values.commerceId && values.zoneId) {
      setTimeout(() => {
        setFieldValue("zoneId", "");
      }, 0);
    }
  }, [values.commerceId, values.zoneId, setFieldValue]);

  // Limpiar dispositivo cuando cambia la zona
  useEffect(() => {
    if (!values.zoneId && values.deviceId) {
      setTimeout(() => {
        setFieldValue("deviceId", "");
      }, 0);
    }
  }, [values.zoneId, values.deviceId, setFieldValue]);

  // Columnas DataTable
  const columns = [
    { Header: "Duración", accessor: "duracion", width: "15%", align: "center" },
    { Header: "Zona", accessor: "zona", width: "15%", align: "center" },
    { Header: "Equipo", accessor: "equipo", width: "15%", align: "center" },
    { Header: "Actividad", accessor: "actividad", width: "15%", align: "center" },
    { Header: "Descripción", accessor: "descripcion", width: "35%", align: "center" },
    { Header: "Acciones", accessor: "acciones", width: "10%", align: "center" },
  ];

  // Filas DataTable
  const rows = actividades.map((act, index) => ({
    duracion: <MDTypography variant="caption">{act.duracion}</MDTypography>,
    zona: <MDTypography variant="caption">{act.zone}</MDTypography>,
    equipo: <MDTypography variant="caption">{act.device}</MDTypography>,
    actividad: <MDTypography variant="caption">{act.activity}</MDTypography>,
    descripcion: (
      <MDTypography variant="caption" sx={{ whiteSpace: "normal", wordBreak: "break-word" }}>
        {act.description}
      </MDTypography>
    ),
    acciones: (
      <MDBox display="flex" justifyContent="center">
        <Tooltip title="Eliminar actividad">
          <IconButton color="error" onClick={() => eliminarActividad(index)}>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </MDBox>
    ),
  }));

  return (
    <Card>
      {/* Fecha actual */}
      <MDBox pt={1} px={2} pb={3}>
        <MDTypography variant="body2" color="text">
          <strong>Fecha/Hora Actual:</strong> {new Date().toLocaleString()}
        </MDTypography>
      </MDBox>

      {/* Formulario principal */}
      <MDBox component="form" onSubmit={handleSubmit} px={2} pb={3}>
        <Grid container spacing={2}>
          {/* --- Campos principales --- */}
          {/* Fecha */}
          <Grid item xs={12} sm={4}>
            <TextField
              label="Fecha y Hora del agendamiento *"
              type="datetime-local"
              name="fechaHora"
              value={values.fechaHora || ""}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.fechaHora && Boolean(errors.fechaHora)}
              helperText={touched.fechaHora && errors.fechaHora}
              fullWidth
              InputLabelProps={{ shrink: true }}
              disabled={isSubmitting || externalLoading}
            />
          </Grid>

          {/* Técnico */}
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth error={touched.technicalId && Boolean(errors.technicalId)}>
              <InputLabel id="technical-label">Técnico a cargo *</InputLabel>
              <Select
                labelId="technical-label"
                name="technicalId"
                value={values.technicalId || ""}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={loadingData || isSubmitting || externalLoading}
                sx={{ height: 45 }}
                label="Técnico a cargo *"
              >
                {loadingData ? (
                  <MenuItem disabled>Cargando técnicos...</MenuItem>
                ) : errorData ? (
                  <MenuItem disabled>{errorData}</MenuItem>
                ) : (
                  tecnicos.map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.username}
                    </MenuItem>
                  ))
                )}
              </Select>
              <FormHelperText>{touched.technicalId && errors.technicalId}</FormHelperText>
            </FormControl>
          </Grid>

          {/* Comercio */}
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth error={touched.commerceId && Boolean(errors.commerceId)}>
              <InputLabel id="commerce-label">Comercio *</InputLabel>
              <Select
                labelId="commerce-label"
                name="commerceId"
                value={values.commerceId || ""}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={loadingData || isSubmitting || externalLoading}
                sx={{ height: 45 }}
                label="Comercio *"
              >
                {loadingData ? (
                  <MenuItem disabled>Cargando comercios...</MenuItem>
                ) : errorData ? (
                  <MenuItem disabled>{errorData}</MenuItem>
                ) : (
                  commerces.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))
                )}
              </Select>
              <FormHelperText>{touched.commerceId && errors.commerceId}</FormHelperText>
            </FormControl>
          </Grid>

          {/* --- Actividades --- */}
          <Grid item xs={12}>
            <MDTypography variant="body2">
              <strong>
                Actividades: (Puede agregar una o más actividades, siempre para el mismo comercio).
              </strong>
            </MDTypography>
          </Grid>

          {/* Duración */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              rows={3}
              name="duracion"
              label="Duración *"
              value={values.duracion || ""}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.duracion && Boolean(errors.duracion)}
              helperText={touched.duracion && errors.duracion}
              disabled={isSubmitting || externalLoading}
            />
          </Grid>

          {/* Zona */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth error={touched.zoneId && Boolean(errors.zoneId)}>
              <InputLabel id="zone-label">Zona *</InputLabel>
              <Select
                labelId="zone-label"
                name="zoneId"
                value={values.zoneId || ""}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={!values.commerceId || loadingData || isSubmitting || externalLoading}
                sx={{ height: 45 }}
                label="Zona *"
              >
                {!values.commerceId ? (
                  <MenuItem disabled>Seleccione un comercio primero</MenuItem>
                ) : filteredZones.length === 0 ? (
                  <MenuItem disabled>No hay zonas disponibles</MenuItem>
                ) : (
                  filteredZones.map((z) => (
                    <MenuItem key={z.id} value={z.id}>
                      {z.title}
                    </MenuItem>
                  ))
                )}
              </Select>
              <FormHelperText>{touched.zoneId && errors.zoneId}</FormHelperText>
            </FormControl>
          </Grid>

          {/* Equipos */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth error={touched.deviceId && Boolean(errors.deviceId)}>
              <InputLabel id="device-label">Equipo en la zona *</InputLabel>
              <Select
                labelId="device-label"
                name="deviceId"
                value={values.deviceId || ""}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={!values.zoneId || loadingData || isSubmitting || externalLoading}
                sx={{ height: 45 }}
                label="Equipo en la zona *"
              >
                {!values.zoneId ? (
                  <MenuItem disabled>Seleccione una zona primero</MenuItem>
                ) : filteredDevices.length === 0 ? (
                  <MenuItem disabled>No hay equipos disponibles</MenuItem>
                ) : (
                  filteredDevices.map((d) => (
                    <MenuItem key={d.id} value={d.id}>
                      {d.nomenclature}
                    </MenuItem>
                  ))
                )}
              </Select>
              <FormHelperText>{touched.deviceId && errors.deviceId}</FormHelperText>
            </FormControl>
          </Grid>

          {/* Tipo de actividad */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth error={touched.activityTypeId && Boolean(errors.activityTypeId)}>
              <InputLabel id="activity-label">Tipo de actividad *</InputLabel>
              <Select
                labelId="activity-label"
                name="activityTypeId"
                value={values.activityTypeId || ""}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={loadingData || isSubmitting || externalLoading}
                sx={{ height: 45 }}
                label="Tipo de actividad *"
              >
                {loadingData ? (
                  <MenuItem disabled>Cargando actividades...</MenuItem>
                ) : errorData ? (
                  <MenuItem disabled>{errorData}</MenuItem>
                ) : (
                  activityTypes.map((a) => (
                    <MenuItem key={a.id} value={a.id}>
                      {a.title}
                    </MenuItem>
                  ))
                )}
              </Select>
              <FormHelperText>{touched.activityTypeId && errors.activityTypeId}</FormHelperText>
            </FormControl>
          </Grid>

          {/* Descripción + botón Agregar */}
          <Grid item xs={12} sm={11}>
            <TextField
              fullWidth
              multiline
              rows={3}
              name="description"
              label="Descripción *"
              value={values.description || ""}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.description && Boolean(errors.description)}
              helperText={touched.description && errors.description}
              disabled={isSubmitting || externalLoading}
            />
          </Grid>
          <Grid item xs={12} sm={1} display="flex" alignItems="center">
            <MDButton
              variant="contained"
              color="info"
              onClick={() => agregarActividad(zones, devices, activityTypes)}
              fullWidth
              disabled={isSubmitting || externalLoading}
              sx={{ height: "100%" }}
            >
              Agregar
            </MDButton>
          </Grid>

          {/* Tabla actividades */}
          {actividades.length > 0 && (
            <Grid item xs={12}>
              <DataTable
                table={{ columns, rows }}
                isSorted
                entriesPerPage={false}
                showTotalEntries
                noEndBorder
              />
            </Grid>
          )}

          {/* Botón guardar */}
          <Grid item xs={12}>
            <MDButton
              type="submit"
              variant="contained"
              color="success"
              fullWidth
              disabled={isSubmitting || actividades.length === 0 || externalLoading}
            >
              {isSubmitting ? "Guardando..." : "Guardar Agendamiento"}
            </MDButton>
          </Grid>
        </Grid>
      </MDBox>

      {/* Notificaciones */}
      <Snackbar open={!!submitError} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert severity="error" onClose={handleCloseSnackbar}>
          {submitError}
        </Alert>
      </Snackbar>
      <Snackbar open={submitSuccess} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert severity="success" onClose={handleCloseSnackbar}>
          ¡Agendamiento {isEditMode ? "actualizado" : "enviado"} correctamente!
        </Alert>
      </Snackbar>
    </Card>
  );
};

FormSchedule.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  afterSubmit: PropTypes.func,
  initialData: PropTypes.shape({
    fechaHora: PropTypes.string,
    technicalId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    commerceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    zoneId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    deviceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    activityTypeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    duracion: PropTypes.string,
    description: PropTypes.string,
    status: PropTypes.bool,
  }),
  isEditMode: PropTypes.bool,
  loading: PropTypes.bool,
};

FormSchedule.defaultProps = {
  afterSubmit: () => {},
  initialData: { status: true },
  isEditMode: false,
  loading: false,
};

export default FormSchedule;
